import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// My account's own screen and panels (S6.05): one key per string, in English
// and Spanish. `useMyAccountCopy` (behavior/use-my-account-copy.ts) reads the
// active language; a key with no Spanish entry falls back to English.
// `account-error-copy.ts` and `passkey-copy.ts` carry their own catalogues (error codes and a
// passkey's added-on summary).

export type MyAccountCatalog = typeof EN;

const EN = {
  myAccount: "My Account",
  security: "Security",
  securityDescription: "How you sign in, and where you are signed in.",
  usage: "Usage",
  usageDescription: "What your plan has used this period.",
  home: "Home",
  profile: "Profile",
  preferences: "Preferences",
  language: "Language",
  comingSoon: "coming soon",
  searchTimeZones: "Search a city or GMT offset",
  noTimeZoneMatch: "No time zone matches",
  currency: "Currency",
  timeZone: "Time zone",
  earlyRelease: "Early release",
  earlyReleaseDescription: "Get included on early releases for new features.",
  preferencesSaved: "Preferences saved",
  uploading: "Uploading…",
  uploadPicture: "Upload picture",
  remove: "Remove",
  avatarHint: "PNG, JPEG or WebP, up to 500KB.",
  fullName: "Full name",
  email: "Email",
  emailHint: "Change your email from the panel below.",
  saveChanges: "Save changes",
  profileUpdated: "Profile updated",
  emailPreferences: "Email preferences",
  emailPreferencesDescription: "Turning one of these off stops that email from being sent.",
  productUpdates: "Product updates",
  productUpdatesDescription: "New features and changes to {product}.",
  tips: "Tips",
  tipsDescription: "Ideas for getting more out of {product}.",
  notificationEmailsManagedFrom:
    "Notification emails (scans, credits, invites) and their digest are managed from",
  notificationSettings: "Notification settings",
  accountStatus: "Account Status",
  upgradePlan: "Upgrade Plan",
  credits: "Credits",
  aiCredits: "AI Credits",
  campaigns: "Campaigns",
  emailAccounts: "Email Accounts",
  signInMethods: "Sign-in methods",
  signInMethodsDescription: "Manage how you sign in. Link Google or LinkedIn to sign in with it as well.",
  emailAndPassword: "Email & password",
  connected: "Connected",
  notConnected: "Not connected",
  canSignInWithIt: "You can sign in with it",
  google: "Google",
  disconnect: "Disconnect",
  connect: "Connect",
  googleDisconnected: "Google disconnected",
  linkedin: "LinkedIn",
  linkedinDisconnected: "LinkedIn disconnected",
  dangerZone: "Danger Zone",
  dangerZoneDescription:
    "Erasing your account cannot be undone once the grace period ends. Please be certain you want to do this.",
  eraseMyAccountAndData: "Erase my account and data",
  eraseAccountIntro:
    "This erases your profile, preferences, campaigns, analyses and every other personal record. Invoices are kept for tax purposes with your details cleared, and the audit trail keeps an anonymous record of account activity.",
  eraseAccountGrace:
    "You'll be signed out everywhere. Sign in again within 14 days to keep your account; after that it is erased for good.",
  eraseConfirmEmailLabel: "Type {email} to confirm",
  blockedTitle: "An organization still needs you",
  blockedIntro:
    "You administer these organizations alongside other members. Remove the other members, transfer the admin role, or leave each one first:",
  gotIt: "Got it",
  accountWillBeErased: "Your account will be erased",
  signInAgainBefore: "Sign in again before {date} to keep it. After that, it is erased for good.",
  sessionEnded: "Session ended",
  passwordChanged: "Password changed",
  signedOutEverywhereElse: "You were signed out everywhere else.",
  // Sessions panel
  whereSignedIn: "Where you're signed in",
  whereSignedInDescription: "Every device with an active session. End one, or sign out everywhere.",
  signOutEverywhere: "Sign out everywhere",
  started: "Started",
  lastSeen: "Last seen",
  thisDevice: "This device",
  end: "End",
  signOutEverywhereDialogDescription:
    "This ends every session, including this device. You will need to sign in again.",
  // Change password panel
  changePassword: "Change password",
  changePasswordDescription: "Ends every other session you are signed in from.",
  currentPassword: "Current password",
  newPassword: "New password",
  passwordHint: "8+ characters",
  changingPassword: "Changing password...",
  // Change email panel
  changeEmail: "Change email",
  changeEmailDescription:
    "You sign in with {email}. Changing it keeps this address working until the new one is confirmed.",
  confirmationSentTo: "A confirmation link was sent to {newEmail}, and a notice to {currentEmail}.",
  newEmail: "New email",
  sendingConfirmation: "Sending confirmation…",
  sendConfirmationLink: "Send confirmation link",
  // Passkeys panel
  passkeys: "Passkeys",
  passkeysDescription: "Sign in with no password, using your device's screen lock or security key.",
  addAPasskey: "Add a passkey",
  noPasskeysYet: "No passkeys yet",
  noPasskeysYetDescription: "Add one to sign in without typing a password.",
  rename: "Rename",
  removePasskeyDialogTitle: "Remove this passkey",
  removePasskeyDialogDescription:
    "You will no longer be able to sign in with it. You can add it again later.",
  removePasskey: "Remove passkey",
  renamePasskeyDialogTitle: "Rename passkey",
  cancel: "Cancel",
  save: "Save",
  name: "Name",
  passkeyNameHint: "Up to 60 characters",
  // Titles, languages and failures
  screenTitleMyAccount: "My Account · {product}",
  screenTitleSecurity: "Security · {product}",
  screenTitleUsage: "Usage · {product}",
  languageEnglish: "English (US)",
  languageSpanish: "Español",
  languagePortuguese: "Português",
  languageItalian: "Italiano",
  languageFrench: "Français",
  languageGerman: "Deutsch",
  currencyEur: "EUR (€)",
  currencyUsd: "USD ($)",
  passkeyNamePlaceholder: "MacBook Touch ID",
  couldNotSaveProfile: "Could not save your profile",
  couldNotSavePreferences: "Could not save your preferences",
  couldNotSavePreference: "Could not save your preference",
  avatarUpdated: "Avatar updated",
  avatarRemoved: "Avatar removed",
  couldNotUploadImage: "Could not upload that image",
  couldNotRemoveAvatar: "Could not remove the avatar",
  couldNotReachProvider: "Could not reach {provider}",
  couldNotDisconnectProvider: "Could not disconnect {provider}",
  couldNotEndSession: "Could not end that session",
  couldNotSignOutEverywhere: "Could not sign out everywhere",
  couldNotChangePassword: "Could not change your password",
  couldNotChangeEmail: "Could not change your email",
  couldNotEraseAccount: "Could not erase your account",
  passkeyAdded: "Passkey added",
  couldNotAddPasskey: "Could not add that passkey",
  couldNotRenamePasskey: "Could not rename that passkey",
  couldNotRemovePasskey: "Could not remove that passkey",
} as const;

const ES: Translation<MyAccountCatalog> = {
  myAccount: "Mi cuenta",
  security: "Seguridad",
  securityDescription: "Cómo inicias sesión y dónde tienes la sesión abierta.",
  usage: "Uso",
  usageDescription: "Lo que tu plan ha usado en este periodo.",
  home: "Inicio",
  profile: "Perfil",
  preferences: "Preferencias",
  language: "Idioma",
  comingSoon: "próximamente",
  searchTimeZones: "Busca una ciudad o un desfase GMT",
  noTimeZoneMatch: "Ninguna zona horaria coincide",
  currency: "Moneda",
  timeZone: "Zona horaria",
  earlyRelease: "Acceso anticipado",
  earlyReleaseDescription: "Recibe primero las funciones nuevas.",
  preferencesSaved: "Preferencias guardadas",
  uploading: "Subiendo…",
  uploadPicture: "Subir foto",
  remove: "Quitar",
  avatarHint: "PNG, JPEG o WebP, hasta 500KB.",
  fullName: "Nombre completo",
  email: "Correo electrónico",
  emailHint: "Cambia tu correo desde el panel de abajo.",
  saveChanges: "Guardar cambios",
  profileUpdated: "Perfil actualizado",
  emailPreferences: "Preferencias de correo",
  emailPreferencesDescription: "Desactivar una de estas opciones deja de enviar ese correo.",
  productUpdates: "Novedades del producto",
  productUpdatesDescription: "Funciones nuevas y cambios en {product}.",
  tips: "Consejos",
  tipsDescription: "Ideas para aprovechar mejor {product}.",
  notificationEmailsManagedFrom:
    "Los correos de notificación (escaneos, créditos, invitaciones) y su resumen se gestionan desde",
  notificationSettings: "Configuración de notificaciones",
  accountStatus: "Estado de la cuenta",
  upgradePlan: "Mejorar plan",
  credits: "Créditos",
  aiCredits: "Créditos de IA",
  campaigns: "Campañas",
  emailAccounts: "Cuentas de correo",
  signInMethods: "Métodos de inicio de sesión",
  signInMethodsDescription:
    "Gestiona cómo inicias sesión. Vincula Google o LinkedIn para iniciar sesión también con ellos.",
  emailAndPassword: "Correo y contraseña",
  connected: "Conectado",
  notConnected: "No conectado",
  canSignInWithIt: "Puedes usarlo para iniciar sesión",
  google: "Google",
  disconnect: "Desconectar",
  connect: "Conectar",
  googleDisconnected: "Google desconectado",
  linkedin: "LinkedIn",
  linkedinDisconnected: "LinkedIn desconectado",
  dangerZone: "Zona de riesgo",
  dangerZoneDescription:
    "Borrar tu cuenta no se puede deshacer una vez que termina el periodo de gracia. Asegúrate de querer hacerlo.",
  eraseMyAccountAndData: "Borrar mi cuenta y mis datos",
  eraseAccountIntro:
    "Esto borra tu perfil, preferencias, campañas, análisis y cualquier otro registro personal. Las facturas se conservan por motivos fiscales sin tus datos personales, y el registro de auditoría conserva un historial anónimo de la actividad de la cuenta.",
  eraseAccountGrace:
    "Se cerrará tu sesión en todas partes. Inicia sesión otra vez dentro de 14 días para conservar tu cuenta; después de eso se borra para siempre.",
  eraseConfirmEmailLabel: "Escribe {email} para confirmar",
  blockedTitle: "Una organización todavía te necesita",
  blockedIntro:
    "Administras estas organizaciones junto a otros miembros. Quita a los otros miembros, transfiere el rol de administrador, o sal de cada una primero:",
  gotIt: "Entendido",
  accountWillBeErased: "Tu cuenta será borrada",
  signInAgainBefore:
    "Inicia sesión otra vez antes del {date} para conservarla. Después de eso, se elimina para siempre.",
  sessionEnded: "Sesión finalizada",
  passwordChanged: "Contraseña cambiada",
  signedOutEverywhereElse: "Se cerró tu sesión en todas las demás partes.",
  whereSignedIn: "Dónde iniciaste sesión",
  whereSignedInDescription:
    "Cada dispositivo con una sesión activa. Cierra una, o cierra sesión en todas partes.",
  signOutEverywhere: "Cerrar sesión en todas partes",
  started: "Iniciada",
  lastSeen: "Última vez",
  thisDevice: "Este dispositivo",
  end: "Finalizar",
  signOutEverywhereDialogDescription:
    "Esto cierra toda sesión, incluida la de este dispositivo. Tendrás que iniciar sesión otra vez.",
  changePassword: "Cambiar contraseña",
  changePasswordDescription: "Cierra toda otra sesión en la que estés conectado.",
  currentPassword: "Contraseña actual",
  newPassword: "Contraseña nueva",
  passwordHint: "8 caracteres o más",
  changingPassword: "Cambiando contraseña...",
  changeEmail: "Cambiar correo",
  changeEmailDescription:
    "Inicias sesión con {email}. Cambiarlo mantiene esta dirección funcionando hasta que se confirme la nueva.",
  confirmationSentTo: "Se envió un enlace de confirmación a {newEmail}, y un aviso a {currentEmail}.",
  newEmail: "Correo nuevo",
  sendingConfirmation: "Enviando confirmación…",
  sendConfirmationLink: "Enviar enlace de confirmación",
  passkeys: "Llaves de acceso",
  passkeysDescription:
    "Inicia sesión sin contraseña, usando el bloqueo de pantalla o la llave de seguridad de tu dispositivo.",
  addAPasskey: "Agregar una llave de acceso",
  noPasskeysYet: "Aún no hay llaves de acceso",
  noPasskeysYetDescription: "Agrega una para iniciar sesión sin escribir una contraseña.",
  rename: "Renombrar",
  removePasskeyDialogTitle: "Quitar esta llave de acceso",
  removePasskeyDialogDescription:
    "Ya no podrás iniciar sesión con ella. Puedes agregarla otra vez más tarde.",
  removePasskey: "Quitar llave de acceso",
  renamePasskeyDialogTitle: "Renombrar llave de acceso",
  cancel: "Cancelar",
  save: "Guardar",
  name: "Nombre",
  passkeyNameHint: "Hasta 60 caracteres",
  screenTitleMyAccount: "Mi cuenta · {product}",
  screenTitleSecurity: "Seguridad · {product}",
  screenTitleUsage: "Uso · {product}",
  languageEnglish: "English (US)",
  languageSpanish: "Español",
  languagePortuguese: "Português",
  languageItalian: "Italiano",
  languageFrench: "Français",
  languageGerman: "Deutsch",
  currencyEur: "EUR (€)",
  currencyUsd: "USD ($)",
  passkeyNamePlaceholder: "MacBook Touch ID",
  couldNotSaveProfile: "No se pudo guardar tu perfil",
  couldNotSavePreferences: "No se pudieron guardar tus preferencias",
  couldNotSavePreference: "No se pudo guardar tu preferencia",
  avatarUpdated: "Foto actualizada",
  avatarRemoved: "Foto eliminada",
  couldNotUploadImage: "No se pudo subir esa imagen",
  couldNotRemoveAvatar: "No se pudo eliminar la foto",
  couldNotReachProvider: "No se pudo conectar con {provider}",
  couldNotDisconnectProvider: "No se pudo desconectar {provider}",
  couldNotEndSession: "No se pudo finalizar esa sesión",
  couldNotSignOutEverywhere: "No se pudo cerrar sesión en todas partes",
  couldNotChangePassword: "No se pudo cambiar tu contraseña",
  couldNotChangeEmail: "No se pudo cambiar tu correo",
  couldNotEraseAccount: "No se pudo borrar tu cuenta",
  passkeyAdded: "Llave de acceso agregada",
  couldNotAddPasskey: "No se pudo agregar esa llave de acceso",
  couldNotRenamePasskey: "No se pudo renombrar esa llave de acceso",
  couldNotRemovePasskey: "No se pudo quitar esa llave de acceso",
};

export const MY_ACCOUNT_CATALOGS: Catalogs<MyAccountCatalog> = { en: EN, es: ES };
